import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import { Button } from './button';
import { cn } from './utils';

/* -------------------------------------------------------------------------- */
/*  Primitives                                                                */
/* -------------------------------------------------------------------------- */

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;

/* -------------------------------------------------------------------------- */
/*  Overlay                                                                   */
/* -------------------------------------------------------------------------- */

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/* -------------------------------------------------------------------------- */
/*  Content                                                                   */
/* -------------------------------------------------------------------------- */

export type DialogMaxWidth = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';

// Widened baseline — every size steps up so modals actually use the space
// they need. Previously `lg` was 512px which crammed line-item tables into
// a scroll box; now `lg` is 960px, which is what a "New delivery / New
// order" form actually needs. Individual dialogs can still opt into `sm`
// for a small confirm, or `full` for very wide grids.
const maxWidthClasses: Record<DialogMaxWidth, string> = {
  sm: 'max-w-[480px]',
  md: 'max-w-[720px]',
  lg: 'max-w-[960px]',
  xl: 'max-w-[1140px]',
  '2xl': 'max-w-[1280px]',
  '3xl': 'max-w-[1440px]',
  full: 'max-w-[calc(100vw-2rem)]',
};

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Controls the panel width. Defaults to `lg` (max-w-lg). */
  maxWidth?: DialogMaxWidth;
  /** Show the top-right close button. Defaults to `true`. */
  showCloseButton?: boolean;
}

export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    { className, children, maxWidth = 'lg', showCloseButton = true, ...props },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Position — centered
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          // Sizing
          'w-full',
          maxWidthClasses[maxWidth],
          // Surface — token-driven
          'bg-card text-foreground',
          'border border-border shadow-lg rounded-xl',
          'p-0',
          // Focus outline suppression — Radix will focus the panel on open
          'focus:outline-none',
          // Animation
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          'data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-2',
          'duration-200',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton ? <DialogClose /> : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = 'DialogContent';

/* -------------------------------------------------------------------------- */
/*  Close button (top-right X)                                                */
/* -------------------------------------------------------------------------- */

export interface DialogCloseProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Close> {
  /** Accessible label for screen readers. */
  srLabel?: string;
}

export const DialogClose = forwardRef<
  ElementRef<typeof DialogPrimitive.Close>,
  DialogCloseProps
>(({ className, srLabel = 'Close', ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    className={cn(
      'absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center',
      'rounded-lg text-muted-foreground',
      'transition-colors hover:bg-accent hover:text-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
      'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      'disabled:pointer-events-none',
      className,
    )}
    {...props}
  >
    <X className="h-4 w-4" aria-hidden />
    <span className="sr-only">{srLabel}</span>
  </DialogPrimitive.Close>
));
DialogClose.displayName = 'DialogClose';

/* -------------------------------------------------------------------------- */
/*  Header / Title / Description / Body / Footer                              */
/* -------------------------------------------------------------------------- */

export const DialogHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col space-y-1 px-6 py-4 border-b border-border',
        // Keep room for the close button on the right.
        'pr-14',
        className,
      )}
      {...props}
    />
  ),
);
DialogHeader.displayName = 'DialogHeader';

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-base font-semibold text-foreground leading-6', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('mt-1 text-[13px] text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

export const DialogBody = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'px-6 py-4 max-h-[70vh] overflow-y-auto text-sm text-foreground',
        className,
      )}
      {...props}
    />
  ),
);
DialogBody.displayName = 'DialogBody';

export const DialogFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col-reverse gap-2 px-6 py-4 border-t border-border bg-muted/20',
        'sm:flex-row sm:justify-end sm:gap-2 rounded-b-xl',
        className,
      )}
      {...props}
    />
  ),
);
DialogFooter.displayName = 'DialogFooter';

/* -------------------------------------------------------------------------- */
/*  ConfirmDialog                                                             */
/* -------------------------------------------------------------------------- */

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void | Promise<void>;
  variant?: 'default' | 'danger';
  loading?: boolean;
  /** Optional max-width override for the underlying panel. */
  maxWidth?: DialogMaxWidth;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  variant = 'default',
  loading = false,
  maxWidth = 'md',
}: ConfirmDialogProps) {
  const handleConfirm = async () => {
    await onConfirm();
  };

  const handleCancel = () => {
    if (loading) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (loading && !next) return;
        onOpenChange(next);
      }}
    >
      <DialogContent maxWidth={maxWidth}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/*  DrawerDialog — side-panel variant built on Radix Dialog                   */
/* -------------------------------------------------------------------------- */

export type DrawerSide = 'left' | 'right' | 'top' | 'bottom';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const drawerSizeClasses: Record<DrawerSide, Record<DrawerSize, string>> = {
  left: {
    sm: 'w-full max-w-xs',
    md: 'w-full max-w-sm',
    lg: 'w-full max-w-md',
    xl: 'w-full max-w-lg',
    full: 'w-screen',
  },
  right: {
    sm: 'w-full max-w-xs',
    md: 'w-full max-w-sm',
    lg: 'w-full max-w-md',
    xl: 'w-full max-w-lg',
    full: 'w-screen',
  },
  top: {
    sm: 'h-1/4',
    md: 'h-1/3',
    lg: 'h-1/2',
    xl: 'h-2/3',
    full: 'h-screen',
  },
  bottom: {
    sm: 'h-1/4',
    md: 'h-1/3',
    lg: 'h-1/2',
    xl: 'h-2/3',
    full: 'h-screen',
  },
};

const drawerSideClasses: Record<DrawerSide, string> = {
  left: 'inset-y-0 left-0 h-full border-r rounded-r-xl',
  right: 'inset-y-0 right-0 h-full border-l rounded-l-xl',
  top: 'inset-x-0 top-0 w-full border-b rounded-b-xl',
  bottom: 'inset-x-0 bottom-0 w-full border-t rounded-t-xl',
};

const drawerAnimationClasses: Record<DrawerSide, string> = {
  left: 'data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
  right:
    'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
  top: 'data-[state=open]:slide-in-from-top data-[state=closed]:slide-out-to-top',
  bottom:
    'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
};

export interface DrawerDialogProps
  extends ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Side of the viewport the drawer slides in from. Defaults to `right`. */
  side?: DrawerSide;
  /** Drawer size on the cross axis (width for left/right, height for top/bottom). */
  size?: DrawerSize;
  /** Show the top-right close button. Defaults to `true`. */
  showCloseButton?: boolean;
}

export const DrawerDialog = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  DrawerDialogProps
>(
  (
    {
      className,
      children,
      side = 'right',
      size = 'md',
      showCloseButton = true,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50 flex flex-col',
          'bg-card text-foreground',
          'border-border shadow-lg',
          'focus:outline-none',
          drawerSideClasses[side],
          drawerSizeClasses[side][size],
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          drawerAnimationClasses[side],
          'duration-200',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton ? <DialogClose /> : null}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DrawerDialog.displayName = 'DrawerDialog';
